// SourceResults.jsx - Module principal qui gère l'état et la coordination
import React, { useState, useEffect } from 'react';
import { Alert, CircularProgress, Typography } from '@mui/material';
import sourceService from './SourceService';
import SourceList from './SourceList';
import SourceDialogs from './SourceDialog';

export default function SourceResults({ 
  sources = [], 
  metadata = {}, 
  loading = false,
  error = null,
  onSave, 
  onDelete, 
  userAuthToken,
  userId,
  competenceId = null
}) {
  // États principaux des sources
  const [selectedSources, setSelectedSources] = useState([]);
  const [filteredSources, setFilteredSources] = useState(sources);
  
  // États pour les opérations
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // États pour les filtres et la pagination
  const [filterType, setFilterType] = useState('');
  const [sortField, setSortField] = useState('relevanceScore');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // États pour les dialogues
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [currentSource, setCurrentSource] = useState(null);
  const [editedSource, setEditedSource] = useState({});
  
  // ==========================================
  // MODIFICATION 1: useEffect pour normaliser les sources avec URLs recommandées
  // ==========================================
  useEffect(() => {
    // Normaliser les sources avec URLs recommandées
    const normalizedSources = sources.map(source => {
      // Si la source vient du backend avec recommendedUrl
      if (source.recommendedUrl) {
        return {
          ...source,
          usableUrl: source.recommendedUrl,
          originalUrl: source.originalUrl || source.url || source.link,
          hasUrlFallback: !!source.fallbackApplied,
          accessibilityStatus: source.accessibilityStatus || 'verified'
        };
      }
      
      // Sinon, normaliser avec l'URL existante
      const usableUrl = source.url || source.link || `https://${source.domain}`;
      return {
        ...source,
        usableUrl: usableUrl,
        originalUrl: usableUrl,
        recommendedUrl: usableUrl,
        hasUrlFallback: false,
        accessibilityStatus: 'not_verified'
      };
    });
    
    setFilteredSources(normalizedSources);
    setSelectedSources(normalizedSources);
  }, [sources]);
  
  // ==========================================
  // MODIFICATION 2: Nouvelle fonction pour vérifier l'accessibilité
  // ==========================================
  const handleCheckAccessibility = async (source) => {
    if (!userAuthToken) {
      console.warn('Token d\'authentification manquant pour la vérification d\'accessibilité');
      return;
    }
    
    try {
      console.log('Vérification d\'accessibilité pour:', source.domain);
      
      // Utiliser la fonction checkUrlAccessibility existante améliorée
      const result = await sourceService.checkUrlAccessibility(
        source.originalUrl || source.usableUrl,
        userAuthToken,
        'basic' // Type de test
      );
      
      console.log('Résultat de vérification d\'accessibilité:', result);
      
      if (result.success) {
        // Mettre à jour la source avec les informations d'accessibilité
        const updatedSources = selectedSources.map(s => 
          s.domain === source.domain ? {
            ...s,
            // Conserver les informations de vérification
            accessibilityCheck: result,
            // Mettre à jour les URLs
            recommendedUrl: result.url || result.recommendedUrl || s.usableUrl,
            usableUrl: result.url || result.recommendedUrl || s.usableUrl,
            originalUrl: s.originalUrl || result.originalUrl || s.usableUrl,
            // Mettre à jour les statuts
            hasUrlFallback: result.type === 'domain_fallback' || result.fallbackApplied === true,
            accessibilityStatus: result.type || (result.accessible ? 'verified' : 'inaccessible'),
            fallbackApplied: result.type === 'domain_fallback' || result.fallbackApplied === true,
            // Conserver les messages et infos
            userMessage: result.userMessage || s.userMessage || null,
            accessibilityInfo: result.accessibilityInfo || s.accessibilityInfo || {}
          } : s
        );
        
        setSelectedSources(updatedSources);
        applyFilters(updatedSources, filterType);
        
        console.log('Source mise à jour avec informations d\'accessibilité');
      } else {
        console.warn('Échec de la vérification d\'accessibilité:', result.error);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification d\'accessibilité:', error);
    }
  };
  
  // Gestionnaire pour l'édition d'une source
  const handleEditSource = (source) => {
    setCurrentSource(source);
    setEditedSource({
      domain: source.domain || '',
      description: source.description || '',
      summary: source.summary || '',
      summaryLanguage: source.summaryLanguage || '',
      type: source.type || 'Commercial',
      reliability: source.reliability || 'Medium',
      relevanceScore: source.relevanceScore || 5,
      popularityScore: source.popularityScore || 5,
      link: source.link || source.usableUrl || `https://${source.domain}`
    });
    setShowEditDialog(true);
  };
  
  // Gestionnaire pour l'ajout d'une nouvelle source
  const handleAddSource = () => {
    setCurrentSource(null);
    setEditedSource({
      domain: '',
      description: '',
      summary: '',
      summaryLanguage: '',
      type: 'Commercial',
      reliability: 'Medium',
      relevanceScore: 5,
      popularityScore: 5,
      link: ''
    });
    setShowEditDialog(true);
  };
  
  // Gestionnaire pour l'affichage du résumé complet
  const handleShowSummary = (source) => {
    setCurrentSource(source);
    setShowSummaryDialog(true);
  };
  
  // Suppression d'une source
  const handleDeleteSource = (sourceToDelete) => {
    if (onDelete) {
      onDelete(sourceToDelete);
      return;
    }
    
    // Suppression locale
    const updatedSources = selectedSources.filter(s => 
      s.domain !== sourceToDelete.domain || 
      (s.url !== sourceToDelete.url && s.title !== sourceToDelete.title)
    );
    
    setSelectedSources(updatedSources);
    applyFilters(updatedSources, filterType);
  };
  
  // Appliquer les filtres et trier
  const applyFilters = (sourcesToFilter, type) => {
    if (type) {
      setFilteredSources(sourcesToFilter.filter(s => s.type === type));
    } else {
      setFilteredSources(sourcesToFilter);
    }
  };
  
  // Gestionnaire pour changer le filtre par type
  const handleFilterChange = (type) => {
    if (type === filterType) {
      setFilterType('');
      setFilteredSources(selectedSources);
    } else {
      setFilterType(type);
      setFilteredSources(selectedSources.filter(s => s.type === type));
    }
    setPage(0);
  };
  
  // Gestionnaire pour changer le tri
  const handleSortChange = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };
  
  // ==========================================
  // MODIFICATION 3: Mise à jour de handleSaveSourceEdit pour préserver URLs recommandées
  // ==========================================
  const handleSaveSourceEdit = (editedSource) => {
    let updatedSources;
    
    if (currentSource) {
      // Mise à jour d'une source existante - PRÉSERVER LES INFOS D'ACCESSIBILITÉ
      updatedSources = selectedSources.map(s => 
        s.domain === currentSource.domain ? {
          ...s,
          // Appliquer les modifications de l'utilisateur
          ...editedSource,
          relevanceScore: Math.min(10, Math.max(1, parseInt(editedSource.relevanceScore) || 5)),
          popularityScore: Math.min(10, Math.max(1, parseInt(editedSource.popularityScore) || 5)),
          // PRÉSERVER les informations d'URL recommandée et d'accessibilité
          usableUrl: s.usableUrl || editedSource.link || `https://${editedSource.domain}`,
          recommendedUrl: s.recommendedUrl || editedSource.link || `https://${editedSource.domain}`,
          originalUrl: s.originalUrl || editedSource.link || `https://${editedSource.domain}`,
          hasUrlFallback: s.hasUrlFallback || false,
          accessibilityStatus: s.accessibilityStatus || 'not_verified',
          accessibilityInfo: s.accessibilityInfo || {},
          fallbackApplied: s.fallbackApplied || false,
          userMessage: s.userMessage || null
        } : s
      );
    } else {
      // Ajout d'une nouvelle source
      const linkUrl = editedSource.link || `https://${editedSource.domain}`;
      const newSource = {
        ...editedSource,
        relevanceScore: Math.min(10, Math.max(1, parseInt(editedSource.relevanceScore) || 5)),
        popularityScore: Math.min(10, Math.max(1, parseInt(editedSource.popularityScore) || 5)),
        id: `new-${Date.now()}`,
        provider: 'Manual',
        searchMethod: 'Manual',
        // Initialiser les propriétés d'URL pour les nouvelles sources
        usableUrl: linkUrl,
        recommendedUrl: linkUrl,
        originalUrl: linkUrl,
        hasUrlFallback: false,
        accessibilityStatus: 'not_verified',
        accessibilityInfo: {},
        fallbackApplied: false,
        userMessage: null
      };
      updatedSources = [...selectedSources, newSource];
    }
    
    setSelectedSources(updatedSources);
    applyFilters(updatedSources, filterType);
    setShowEditDialog(false);
  };
  
  /**
   * Sauvegarder les sources dans la base de données 
   * Cette fonction détecte s'il faut créer ou mettre à jour
   */
  const handleSaveSources = async () => {
    if (!selectedSources.length || !userId || !userAuthToken) {
      setSaveError("Impossible de sauvegarder: données manquantes");
      return;
    }
    
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    
    try {
      console.log("=== DÉBUT SAUVEGARDE ===");
      console.log("Sources à sauvegarder:", selectedSources.length);
      console.log("Métadonnées disponibles:", metadata);
      
      // ÉTAPE 1: Déterminer s'il s'agit d'une mise à jour ou d'une création
      // Vérifier si on a un ID de session existant
      const hasExistingSession = metadata.sourceId || 
                                metadata.sessionId || 
                                (selectedSources.length > 0 && selectedSources[0].sessionId);
      
      console.log("Mode détecté:", hasExistingSession ? "MISE À JOUR" : "CRÉATION");
      
      if (hasExistingSession) {
        // MISE À JOUR d'une session existante
        const sessionId = metadata.sourceId || 
                        metadata.sessionId || 
                        selectedSources[0].sessionId;
                        
        console.log("ID de session pour mise à jour:", sessionId);
        
        if (!sessionId) {
          throw new Error("ID de session manquant pour la mise à jour");
        }
        
        console.log("🔍 DEBUG SELECTEDSOURCES:");
        console.log("1. selectedSources[0]:", selectedSources[0]);
        console.log("2. selectedSources[0].sessionId:", selectedSources[0]?.sessionId);
        console.log("3. Tous ont sessionId:", 
          selectedSources.every(s => !!s.sessionId));
      
        // ==========================================
        // MODIFICATION 4: Préparer les sources nettoyées pour la mise à jour avec URLs recommandées
        // ==========================================
        const cleanedSources = selectedSources.map(source => ({
          domain: source.domain,
          title: source.title,
          description: source.description,
          url: source.url,
          type: source.type,
          language: source.language,
          country: source.country,
          reliability: source.reliability,
          relevanceScore: source.relevanceScore,
          popularityScore: source.popularityScore,
          provider: source.provider,
          searchMethod: source.searchMethod,
          // NOUVEAU: Conserver les informations d'URL recommandée
          recommendedUrl: source.recommendedUrl,
          originalUrl: source.originalUrl,
          usableUrl: source.usableUrl,
          hasUrlFallback: source.hasUrlFallback,
          fallbackApplied: source.fallbackApplied,
          accessibilityStatus: source.accessibilityStatus,
          accessibilityInfo: source.accessibilityInfo,
          userMessage: source.userMessage
          // Note: on retire sessionId et autres métadonnées locales
        }));
        
        console.log("Appel de updateSources avec:", {
          sessionId,
          sourcesCount: cleanedSources.length,
          category: metadata.category
        });
        
        // Appeler l'endpoint UPDATE au lieu de CREATE
        const updateResult = await sourceService.updateSources(
          sessionId,
          cleanedSources,
          metadata.category || "Sources personnalisées",
          userAuthToken,
          competenceId
        );
        
        if (updateResult && updateResult.success) {
          console.log("✅ Mise à jour réussie");
          setSaveSuccess(true);
          
          // Callback vers le parent si disponible
          if (onSave) {
            onSave(selectedSources, updateResult);
          }
        } else {
          throw new Error(updateResult?.message || "Échec de la mise à jour");
        }
        
      } else {
        // CRÉATION d'une nouvelle session
        console.log("Création d'une nouvelle session");
        
        const category = metadata.category || 'Sources personnalisées';
        const providers = Array.from(
          new Set(selectedSources
            .map(s => s.provider || s.searchMethod)
            .filter(Boolean))
        );
        
        // ==========================================
        // MODIFICATION 5: Inclure les URLs recommandées dans la création
        // ==========================================
        const saveParams = {
          userId,
          competenceId,
          userObjective: metadata.userObjective || metadata.title || '',
          query: metadata.query || 'Recherche manuelle',
          sources: selectedSources.map(source => ({
            domain: source.domain,
            title: source.title,
            description: source.description,
            url: source.url,
            type: source.type,
            language: source.language,
            country: source.country,
            reliability: source.reliability,
            relevanceScore: source.relevanceScore,
            popularityScore: source.popularityScore,
            provider: source.provider,
            searchMethod: source.searchMethod,
            // NOUVEAU: Inclure les informations d'URL recommandée
            recommendedUrl: source.recommendedUrl,
            originalUrl: source.originalUrl,
            usableUrl: source.usableUrl,
            hasUrlFallback: source.hasUrlFallback,
            fallbackApplied: source.fallbackApplied,
            accessibilityStatus: source.accessibilityStatus,
            accessibilityInfo: source.accessibilityInfo,
            userMessage: source.userMessage
          })),
          category,
          providers
        };
        
        console.log("Paramètres de création:", {
          userId: saveParams.userId,
          competenceId: saveParams.competenceId,
          sourcesCount: saveParams.sources.length,
          category: saveParams.category
        });
        
        const result = await sourceService.saveSources(saveParams, userAuthToken);
        
        if (result && result.success) {
          console.log("✅ Création réussie, ID:", result.sourceId);
          setSaveSuccess(true);
          
          // Callback vers le parent si disponible
          if (onSave) {
            onSave(selectedSources, result);
          }
        } else {
          throw new Error(result?.message || "Échec de la création");
        }
      }
      
      console.log("=== FIN SAUVEGARDE RÉUSSIE ===");
      
    } catch (error) {
      console.error("=== ERREUR SAUVEGARDE ===", error);
      setSaveError(error.message || 'Erreur lors de la sauvegarde');
      
      // Debug: Afficher les détails de l'erreur
      if (error.response) {
        console.error("Status HTTP:", error.response.status);
        console.error("URL appelée:", error.config?.url);
        console.error("Données de réponse:", error.response.data);
      }
    } finally {
      setSaving(false);
    }
  };
  
  // Afficher un message de chargement
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center my-12 space-y-4">
        <CircularProgress className="text-purple-600" />
        <Typography className="text-gray-600">
          Recherche des meilleures sources...
        </Typography>
      </div>
    );
  }
  
  // Afficher un message d'erreur
  if (error) {
    return (
      <Alert severity="error" className="my-6">
        {error}
      </Alert>
    );
  }
  
  // Si aucune source n'est trouvée
  if (!loading && (!sources || sources.length === 0)) {
    return (
      <Alert severity="info" className="my-6">
        Aucune source trouvée. Veuillez modifier votre recherche.
      </Alert>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* ==========================================
          MODIFICATION 6: Mise à jour du passage des props vers SourceList
          ========================================== */}
      <SourceList 
        sources={filteredSources}
        metadata={metadata}
        filterType={filterType}
        sortField={sortField}
        sortOrder={sortOrder}
        page={page}
        rowsPerPage={rowsPerPage}
        saving={saving}
        saveSuccess={saveSuccess}
        saveError={saveError}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
        onChangePage={setPage}
        onChangeRowsPerPage={setRowsPerPage}
        onEditSource={handleEditSource}
        onDeleteSource={handleDeleteSource}
        onShowSummary={handleShowSummary}
        onAddSource={handleAddSource}
        onSaveSources={handleSaveSources}
        // NOUVELLES PROPS pour l'accessibilité
        onCheckAccessibility={handleCheckAccessibility}
        userAuthToken={userAuthToken}
      />
      
      {/* Dialogues pour l'édition et l'affichage du résumé */}
      <SourceDialogs
        editDialogOpen={showEditDialog}
        summaryDialogOpen={showSummaryDialog}
        source={currentSource}
        editedSource={editedSource}
        onEditClose={() => setShowEditDialog(false)}
        onSummaryClose={() => setShowSummaryDialog(false)}
        onSaveEdit={handleSaveSourceEdit}
      />
    </div>
  );
}